import { Link, type LinkProps } from "react-router-dom";
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "inverted";
type Size = "sm" | "md" | "icon" | "icon-sm";

const base =
  "inline-flex items-center justify-center gap-2 rounded-lg text-sm font-semibold transition " +
  "focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50";

const sizeCls: Record<Size, string> = {
  // A shared 36px control height keeps icon-only and text actions aligned.
  md: "h-9 px-4",
  sm: "h-9 px-3",
  // Keep padding out of this variant entirely so fixed-width SVGs never shrink.
  icon: "size-9 p-0",
  // Lower-emphasis icon actions used within list and subgroup headers.
  "icon-sm": "size-8 p-0",
};

const variantCls: Record<Variant, string> = {
  primary: "bg-brand-600 text-white hover:bg-brand-700 focus-visible:ring-brand-500/40",
  secondary:
    "border border-line bg-white text-ink hover:bg-slate-50 focus-visible:ring-brand-500/20",
  ghost: "text-ink hover:bg-slate-100 focus-visible:ring-brand-500/20",
  danger: "bg-danger text-white hover:bg-danger/90 focus-visible:ring-danger/40",
  // For controls placed on a dark surface (e.g. SelectionActionBar's bottom bar).
  inverted:
    "border border-white/20 bg-transparent text-white hover:bg-white/10 focus-visible:ring-white/40",
};

export function buttonClassName({
  variant = "primary",
  size = "md",
  fullWidth = false,
  className,
}: {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  className?: string;
}): string {
  return [
    base,
    sizeCls[size],
    variantCls[variant],
    fullWidth ? "w-full" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
}

export type ButtonProps = {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  icon?: ReactNode;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export function Button({
  variant = "primary",
  size = "md",
  fullWidth = false,
  icon,
  className,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={buttonClassName({ variant, size, fullWidth, className })}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}

export type LinkButtonProps = {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  icon?: ReactNode;
  className?: string;
  children?: ReactNode;
} & Omit<LinkProps, "className" | "children">;

export function LinkButton({
  variant = "primary",
  size = "md",
  fullWidth = false,
  icon,
  className,
  children,
  ...rest
}: LinkButtonProps) {
  return (
    <Link
      className={`${buttonClassName({ variant, size, fullWidth, className })} no-underline`}
      {...rest}
    >
      {icon}
      {children}
    </Link>
  );
}

// Plain-<a> equivalent of LinkButton for SSR-safe surfaces that must not depend on
// react-router context (D-36) — marketing pages/components use this, not LinkButton.
export type AnchorButtonProps = {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  icon?: ReactNode;
} & AnchorHTMLAttributes<HTMLAnchorElement>;

export function AnchorButton({
  variant = "primary",
  size = "md",
  fullWidth = false,
  icon,
  className,
  children,
  ...rest
}: AnchorButtonProps) {
  return (
    <a
      className={`${buttonClassName({ variant, size, fullWidth, className })} no-underline`}
      {...rest}
    >
      {icon}
      {children}
    </a>
  );
}
